import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import { selectOutletId } from '@store/auth/authSelectors';
import { Download, RefreshCw } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import {
  BookingSourceReportRow,
  fetchBookingSourceReport,
} from '../../../../services/accommodationService';
import { apiError } from '../../../../util/notify';
import { exportToCsv, fmtAmount } from './reportUtils';

export const BookingSourceReportPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const userOutletId = useAppSelector(selectOutletId);
  const outletId = property?.outlet_id ?? userOutletId ?? null;
  const currency = property?.currency ?? '';

  const [rows, setRows] = useState<BookingSourceReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchBookingSourceReport(outletId);
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [outletId]);

  const totalBookings = rows.reduce((s, r) => s + r.total_bookings, 0);
  const totalRevenue = rows.reduce((s, r) => s + r.total_revenue, 0);
  const totalCollected = rows.reduce((s, r) => s + r.total_collected, 0);

  const handleCsv = () => {
    exportToCsv(
      'booking-source-report',
      rows.map((r) => ({
        Source: r.source_name,
        'Total Bookings': r.total_bookings,
        'Confirmed Bookings': r.confirmed_bookings,
        'Cancelled Bookings': r.cancelled_bookings,
        [`Revenue (${currency})`]: fmtAmount(r.total_revenue),
        [`Collected (${currency})`]: fmtAmount(r.total_collected),
      })),
    );
  };

  const COLOURS = [
    'bg-pos-primary',
    'bg-blue-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-teal-500',
    'bg-orange-500',
  ];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <p className="flex-1 text-sm text-gray-500">
          This report aggregates all-time bookings by source.
        </p>
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Loading…' : 'Run report'}
        </button>
        {loaded && rows.length > 0 && (
          <button
            type="button"
            onClick={handleCsv}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {loaded && (
        <>
          {/* KPI summary */}
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                label: 'Total Bookings',
                value: String(totalBookings),
                sub: 'all sources',
              },
              {
                label: 'Total Revenue',
                value: `${currency} ${fmtAmount(totalRevenue)}`,
                sub: 'confirmed only',
              },
              {
                label: 'Total Collected',
                value: `${currency} ${fmtAmount(totalCollected)}`,
                sub: 'payments received',
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-2xl border border-gray-200 bg-white p-4"
              >
                <p className="text-xs text-gray-500">{card.label}</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {card.value}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Distribution chart */}
          {rows.length > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <h4 className="mb-4 text-sm font-semibold text-gray-900">
                Revenue by source
              </h4>
              <div className="space-y-3">
                {rows.map((r, i) => {
                  const pct =
                    totalRevenue > 0
                      ? Math.round((r.total_revenue / totalRevenue) * 100)
                      : 0;
                  return (
                    <div
                      key={r.source_name}
                      className="flex items-center gap-3"
                    >
                      <span className="w-32 shrink-0 truncate text-xs text-gray-600">
                        {r.source_name}
                      </span>
                      <div
                        className="flex-1 overflow-hidden rounded-full bg-gray-100"
                        style={{ height: 10 }}
                      >
                        <div
                          className={`h-full rounded-full ${COLOURS[i % COLOURS.length]} transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-20 shrink-0 text-right text-xs font-medium text-gray-700">
                        {pct}% ({r.confirmed_bookings})
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                Source breakdown
              </h4>
            </div>
            {rows.length === 0 ? (
              <div className="flex h-28 items-center justify-center text-sm text-gray-400">
                No booking source data found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      {[
                        'Source',
                        'Total Bookings',
                        'Confirmed',
                        'Cancelled',
                        `Revenue (${currency})`,
                        `Collected (${currency})`,
                        'Conversion %',
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((r) => {
                      const conversion =
                        r.total_bookings > 0
                          ? Math.round(
                              (r.confirmed_bookings / r.total_bookings) * 100,
                            )
                          : 0;
                      return (
                        <tr key={r.source_name} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2.5 font-medium text-gray-900">
                            {r.source_name}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {r.total_bookings}
                          </td>
                          <td className="px-4 py-2.5 text-green-600">
                            {r.confirmed_bookings}
                          </td>
                          <td className="px-4 py-2.5 text-red-500">
                            {r.cancelled_bookings}
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                            {fmtAmount(r.total_revenue)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-700">
                            {fmtAmount(r.total_collected)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {conversion}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-2.5 text-xs font-semibold text-gray-600"
                      >
                        Total ({rows.length} sources)
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold text-gray-900">
                        {fmtAmount(totalRevenue)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-semibold text-gray-700">
                        {fmtAmount(totalCollected)}
                      </td>
                      <td aria-label="Conversion" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Click &ldquo;Run report&rdquo; to load booking source data.
        </div>
      )}
    </div>
  );
};
